<template>
  <div>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <Panel size="small">
          <template #header>
            <span data-cy="heading">Create Softphone Campaign</span>
          </template>

          <template #content>
            <div>
              <b-alert
                show
                variant="info"
                class="mb-3"
              >
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="mr-2"
                />
                Calls are answered by your agents in the browser, so there is no destination number
                to configure. US calls only.
              </b-alert>

              <BaseFormSelect
                id="buyer-create-campaign__campaign-category"
                v-model="categoryId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                name="Category"
                data-cy="select-category"
                label="Category"
                text-field="displayName"
                value-field="id"
                :options="categoryList"
                required
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />

              <!-- A category with a single language leaves the buyer nothing to choose, so it is
                   preselected and the dropdown stays hidden. -->
              <BaseFormSelect
                v-if="showLanguageSelect"
                id="buyer-create-campaign__campaign-language"
                v-model="languageId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                name="Language"
                data-cy="select-language"
                label="Language"
                text-field="name"
                value-field="id"
                :options="languageOptions"
                required
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />
            </div>
          </template>
        </Panel>

        <Budget
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="confirm-create-softphone-campaign-modal"
      ref="confirm-create-softphone-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Campaign Creation"
      button-size="sm"
      no-close-on-backdrop
      @hidden="termsAccepted = false"
    >
      <p>
        Agents must service all qualified calls in good faith. Valid calls disconnected or rejected to avoid payment may be deemed accepted and billable. Repeated violations may result in force-billing, suspension, or termination of service.
      </p>

      <b-form-checkbox
        v-model="termsAccepted"
        data-cy="checkbox-accept-terms"
        class="mb-0"
      >
        I acknowledge and accept the above terms regarding the handling of qualified calls and the potential consequences of non-compliance.
      </b-form-checkbox>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-create-softphone-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          :disabled="!termsAccepted"
          data-cy="button-confirm-create-campaign"
          @click="submitForm"
        >
          Confirm
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import Budget from './Budget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { ACCOUNT_REQUIREMENTS, dashboardNotificationForAccountRequirementsMessages } from '@/store/modules/auth.store'
import {
  buildSoftphoneCampaignName,
  campaignRoutingTypes,
  destinationTypes,
  resolveSoftphoneCampaignLanguageId,
  SOFTPHONE_CAMPAIGN_COUNTRY_ID
} from '@/store/modules/campaign.store'

export default {
  name: 'BuyerCreateSoftphoneCampaign',

  title: 'Create softphone campaign',

  components: {
    Budget,
  },

  data() {
    return {
      isLoading: false,
      termsAccepted: false
    }
  },

  computed: {
    ...mapState('common-entities', ['categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),

    ...mapState('auth', ['userDetails']),

    ...mapGetters('auth', ['accountTimeZoneId', 'isMinimumInitialDepositRequirementSatisfied']),

    // Same merge of account-allowed and public categories as the full Create Campaign page.
    categoryList() {
      const publicCategories = this.categories.filter(
        publicCategory => !this.allowedCategories.some(
          allowedCategory => allowedCategory.id === publicCategory.id
        )
      )

      const categoryList = this.allowedCategories
        .concat(publicCategories)
        .map(category => ({
          ...category,
          displayName: category.isCpaConversionOnly ? `${category.name} (CPA Only)` : category.name
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))

      categoryList.unshift({ name: 'Please select', displayName: 'Please select', value: null })

      return categoryList
    },

    // Guarded on a truthy id: the "Please select" placeholder has no id, so an unguarded lookup
    // would match it and report a selected category with no languages.
    selectedCategory() {
      return this.buyerCampaign.categoryId
        ? this.categoryList.find(category => category.id === this.buyerCampaign.categoryId) || null
        : null
    },

    // The API rejects a language the category is not configured for, so the choice is always
    // scoped to the selected category's languages.
    categoryLanguages() {
      return this.selectedCategory?.languages ?? []
    },

    // Exactly one language is not a choice - it is preselected and the dropdown is hidden. Zero
    // configured languages is not expected, but showing the (empty) required dropdown fails the
    // form instead of submitting a language the category would reject.
    showLanguageSelect() {
      return Boolean(this.selectedCategory) && this.categoryLanguages.length !== 1
    },

    languageOptions() {
      const languageOptions = [...this.categoryLanguages]

      languageOptions.unshift({ name: 'Please select', value: null })

      return languageOptions
    },

    categoryId: {
      get() {
        return this.buyerCampaign.categoryId
      },

      set(value) {
        const category = this.categoryList.find(c => c.id === value)

        this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
          name: buildSoftphoneCampaignName(category?.name, this.userDetails?.name),
          categoryId: value,
          countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
          languageId: resolveSoftphoneCampaignLanguageId(category?.languages, this.buyerCampaign.languageId)
        })
      }
    },

    languageId: {
      get() {
        return this.buyerCampaign.languageId
      },

      set(value) {
        this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
          name: this.buyerCampaign.name,
          categoryId: this.buyerCampaign.categoryId,
          countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
          languageId: value
        })
      }
    }
  },

  watch: {
    isMinimumInitialDepositRequirementSatisfied: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.showDashboardNotification(dashboardNotificationForAccountRequirementsMessages[ACCOUNT_REQUIREMENTS.MinimumInitialDeposit])
        } else {
          this.hideDashboardNotification()
        }
      }
    },

    // Both of these come from the account details. They are normally loaded before this page
    // mounts, but the buyer cannot see or fix either field here, so re-apply them if they
    // arrive late rather than submitting an empty required value.
    allowedTrafficSources() {
      this.UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES({
        ...this.buyerCampaign.trafficSources,
        allowedSources: this.defaultAllowedSourceIds()
      })
    },

    accountTimeZoneId(timeZoneId) {
      if (this.buyerCampaign.routing.schedule.timeZoneId) {
        return
      }

      this.UPDATE_BUYER_CAMPAIGN_ROUTING({
        ...this.buyerCampaign.routing,
        schedule: {
          ...this.buyerCampaign.routing.schedule,
          timeZoneId
        }
      })
    }
  },

  async created() {
    this.setDefaultBuyerCampaign()
    await this.getCategories()
    this.applySoftphoneDefaults()
  },

  methods: {
    ...mapActions([
      'showDashboardNotification',
      'hideDashboardNotification'
    ]),

    ...mapActions('common-entities', ['getCategories']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'createBuyerCampaign']),

    ...mapMutations('campaign', [
      'UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS',
      'UPDATE_BUYER_CAMPAIGN_ROUTING',
      'UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES'
    ]),

    // Everything the buyer is not asked about. Destination, Schedule & Capacity and Traffic
    // Sources are not rendered on this page, so their values have to be set here or the API
    // rejects the request (schedule.timeZoneId and allowedSources are both required).
    applySoftphoneDefaults() {
      // No category picked yet, so no language is known either - it follows from the category.
      this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
        name: buildSoftphoneCampaignName(null, this.userDetails?.name),
        categoryId: null,
        countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
        languageId: null
      })

      this.UPDATE_BUYER_CAMPAIGN_ROUTING({
        ...this.buyerCampaign.routing,
        type: campaignRoutingTypes.Static,
        destinationType: destinationTypes.Softphone,
        number: '',
        sipNumber: {},
        schedule: {
          ...this.buyerCampaign.routing.schedule,
          timeZoneId: this.accountTimeZoneId
        }
      })

      this.UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES({
        ...this.buyerCampaign.trafficSources,
        allowTollFreeCallers: true,
        restrictDuplicates: 'DoNotRestrict',
        allowThirdParties: true,
        allowedSources: this.defaultAllowedSourceIds()
      })
    },

    // Prefill with every traffic source the account can use for this call type - the same list
    // the hidden Traffic Sources section would have offered.
    defaultAllowedSourceIds() {
      const callType = this.buyerCampaign.trafficSources.callType

      return (this.allowedTrafficSources ?? [])
        .filter(source => !callType || source.trafficSourceType === callType)
        .map(source => source.id)
    },

    showConfirmSubmit() {
      this.termsAccepted = false
      this.$bvModal.show('confirm-create-softphone-campaign-modal')
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.createBuyerCampaign(this.buyerCampaign)

        this.$bvModal.hide('confirm-create-softphone-campaign-modal')

        showSuccessMessage('Campaign created successfully')
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
